import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdQuickPurchaseModal from '@/components/AdQuickPurchaseModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL;

// Supported sizes (desktop) -> mobile fallback
const SIZE_MAP = {
  '970x250': { w: 970, h: 250, mobile: { w: 320, h: 100 } },
  '970x500': { w: 970, h: 500, mobile: { w: 320, h: 200 } },
  '300x250': { w: 300, h: 250 },
  '300x600': { w: 300, h: 600, mobile: { w: 300, h: 250 } },
  '580x300': { w: 580, h: 300, mobile: { w: 320, h: 180 } },
  '320x100': { w: 320, h: 100 },
  '320x250': { w: 320, h: 250 },
};

// Position → preferred slot keyword mapping (match the "slot" field editors
// pick in the /admin/ads "Upravljanje oglasima" module).
const POSITION_TO_SLOT = {
  'homepage-top': 'hero',
  'homepage-hero': 'hero',
  'article-bottom': 'in_article',
  'article-inline': 'in_article',
  'sidebar-top': 'sidebar',
  'sidebar-bottom': 'sidebar',
};

/**
 * Public ad slot. Rendering order:
 *   1) If the admin has published an active ad in /admin/ads whose data.slot
 *      matches this position, show it (clickable to target_url).
 *   2) Otherwise fall back to the "Your ad here" placeholder linking to
 *      /oglasavanje.
 * The component honours the per-slot on/off switch from
 * /api/public/homepage-settings → ads.placeholders.{position}.
 */
export default function AdSlot({ size = '970x250', position = '' }) {
  const { t } = useTranslation();
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [enabled, setEnabled] = useState(true);
  const [ad, setAd] = useState(null);
  const [placement, setPlacement] = useState(null);
  const [showQuick, setShowQuick] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/homepage-settings`).then(r => r.ok ? r.json() : null).then(d => {
      const b = d?.branding || {};
      if (b.footer_logo_url) {
        const u = b.footer_logo_url.startsWith('/api/') ? `${BACKEND_HOST}${b.footer_logo_url}` : b.footer_logo_url;
        setLogoUrl(u);
      }
      const ads = d?.ads?.placeholders || {};
      if (position && Object.prototype.hasOwnProperty.call(ads, position)) setEnabled(ads[position] !== false);
    }).catch(() => {});

    // Prefer the new placement_ads system: a paid (or free admin) ad with
    // uploaded media for this slot wins over the legacy module text-only ad.
    const want = POSITION_TO_SLOT[position];
    if (want) {
      fetch(`${API}/public/placement-ad/${want}`).then(r => r.ok ? r.json() : null).then(d => {
        if (d?.ad) { setPlacement(d.ad); return; }
        // Fallback to legacy module ads (text-only).
        fetch(`${API}/public/modules/ads`).then(r => r.ok ? r.json() : null).then(d2 => {
          const match = (d2?.items || []).find(it => it.data?.slot === want);
          if (match) setAd(match);
        }).catch(() => {});
      }).catch(() => {});
    }
  }, [position]);

  if (!enabled) return null;
  const cfg = SIZE_MAP[size] || SIZE_MAP['970x250'];

  // ── Placement ad with uploaded media (paid > free_admin) ──
  if (placement && placement.media_url) {
    const href = placement.target_url || '/oglasavanje';
    const external = href.startsWith('http');
    const mediaSrc = placement.media_url.startsWith('http') ? placement.media_url : `${BACKEND_HOST}${placement.media_url}`;
    const objectFit = placement.scaling_mode === 'stretch' ? 'fill' : (placement.scaling_mode === 'contain' ? 'contain' : 'cover');
    return (
      <a
        href={href}
        target={external ? '_blank' : '_self'}
        rel={external ? 'noopener noreferrer' : undefined}
        className="ad-slot relative flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900 overflow-hidden group transition-colors hover:border-red-500"
        style={{ width: '100%', maxWidth: cfg.w, aspectRatio: `${cfg.w} / ${cfg.h}` }}
        data-testid={`ad-slot-${position || size}`}
        data-slot-kind="placement"
        data-ad-id={placement.id}
        data-ad-paid={placement.kind === 'paid' ? '1' : '0'}
      >
        {placement.media_type === 'video' ? (
          <video src={mediaSrc} muted autoPlay loop playsInline className="w-full h-full" style={{ objectFit }} />
        ) : (
          <img src={mediaSrc} alt={placement.title || ''} className="w-full h-full" style={{ objectFit }} />
        )}
        {placement.title && (
          <span className="absolute bottom-0 left-0 right-0 z-10 bg-black/55 px-2 py-1 text-[10px] text-white truncate">
            {placement.kind === 'paid' ? 'Oglas' : 'Promo'} · {placement.title}
          </span>
        )}
      </a>
    );
  }

  // ── Admin-published ad ──
  if (ad) {
    const href = ad.data?.target_url || '/oglasavanje';
    const external = href.startsWith('http');
    return (
      <a
        href={href}
        target={external ? '_blank' : '_self'}
        rel={external ? 'noopener noreferrer' : undefined}
        className="ad-slot relative flex items-center justify-center rounded-lg border border-red-500/30 bg-gradient-to-br from-red-950/20 to-zinc-900 overflow-hidden group transition-colors hover:border-red-500"
        style={{ width: '100%', maxWidth: cfg.w, aspectRatio: `${cfg.w} / ${cfg.h}` }}
        data-testid={`ad-slot-${position || size}`}
        data-slot-kind="published"
        data-ad-id={ad.id}
      >
        <div className="relative text-center px-5 z-10">
          <p className="text-[10px] uppercase tracking-[0.18em] text-red-400 font-bold mb-1">
            {ad.data?.kind === 'native' ? 'Sponzorirano' : 'Oglas'}
            {ad.data?.client ? ` · ${ad.data.client}` : ''}
          </p>
          <p className="text-sm sm:text-base font-semibold text-zinc-100 group-hover:text-white">
            {ad.title}
          </p>
          {ad.description && (
            <p className="text-xs text-zinc-400 mt-1 line-clamp-2 max-w-prose mx-auto">
              {ad.description}
            </p>
          )}
        </div>
      </a>
    );
  }

  // ── "Your ad here" placeholder fallback ──
  return (
    <>
    <button type="button" onClick={() => setShowQuick(true)}
      className="ad-slot relative flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-800 bg-[#f4f5f7] dark:bg-[#0f172a] overflow-hidden group transition-colors hover:border-red-400 dark:hover:border-red-600"
      style={{ width: '100%', maxWidth: cfg.w, aspectRatio: `${cfg.w} / ${cfg.h}` }}
      data-testid={`ad-slot-${position || size}`}
      data-slot-kind="placeholder"
      data-size={size}
    >
      {/* Logo centred — fully visible, no text overlay on top of it. */}
      <img src={logoUrl} alt="" className="absolute inset-0 w-full h-full object-contain opacity-[0.4] group-hover:opacity-50 transition-opacity p-8" />
      {/* CTA text fixed to the BOTTOM strip on a translucent background so it
          never sits on top of the logo. Size badge fixed to the TOP strip. */}
      <span className="absolute top-1.5 left-1/2 -translate-x-1/2 z-10 text-[9px] text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-white/70 dark:bg-black/40 px-1.5 py-0.5 rounded">
        {size}
      </span>
      <div className="absolute bottom-0 inset-x-0 z-10 bg-white/80 dark:bg-black/50 backdrop-blur-[2px] px-3 py-1.5 text-center">
        <p className="text-[11px] sm:text-xs font-semibold text-gray-700 dark:text-gray-300">
          {t('ads.your_ad_here')} <span className="text-red-600 group-hover:text-red-700 ml-1">{t('ads.learn_more')} →</span>
        </p>
      </div>
    </button>
    {showQuick && <AdQuickPurchaseModal slot={position} size={size} onClose={() => setShowQuick(false)} />}
    </>
  );
}
